import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { User, CreditCard, ArrowLeftRight, Globe2 } from 'lucide-react';

const offerings = [
  {
    icon: User,
    title: 'Named & Client Sub-Named Accounts',
    description: 'Full control over account naming for businesses and their clients.'
  },
  {
    icon: CreditCard,
    title: 'IBANs & Virtual IBANs',
    description: 'Instant issuance for simplified reconciliation and segregation.'
  },
  {
    icon: ArrowLeftRight,
    title: '1st & 3rd Party Payments',
    description: 'Support for payments on behalf of your business or authorised third parties.'
  },
  {
    icon: Globe2,
    title: 'Multi-Currency Support',
    description: 'EUR, GBP, USD, AED and more via SEPA, SWIFT, CHAPS and FPS.'
  }
];

export const WhatWeOffer = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section 
      className="py-16 lg:py-20 bg-[#E8E8E8] border-t border-[#D1D5DB]"
      data-testid="what-we-offer-section"
    >
      <div className="section-container">
        {/* Section Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="mb-10"
        >
          <span className="label-text text-[#6B7280] block mb-4">
            What We Offer
          </span>
          <h2 
            className="heading-lg text-3xl md:text-4xl lg:text-5xl text-[#1E3A5F]"
            data-testid="what-we-offer-title"
          >
            Accounts. Payments. Infrastructure.
          </h2>
        </motion.div>

        {/* Offerings Grid */}
        <div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E5E7EB] border border-[#E5E7EB]"
          data-testid="offerings-grid"
        >
          {offerings.map((offering, index) => (
            <motion.div
              key={offering.title}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ 
                duration: 0.6, 
                delay: index * 0.1, 
                ease: [0.22, 1, 0.36, 1] 
              }}
              className="bg-white p-8 lg:p-10 card-hover"
              data-testid={`offering-${index}`}
            >
              <offering.icon 
                className="w-6 h-6 text-[#1E3A5F] mb-6" 
                strokeWidth={1.25} 
              />
              <h3 className="text-lg font-semibold text-[#1E3A5F] mb-3">
                {offering.title}
              </h3>
              <p className="text-base text-[#6B7280] leading-relaxed">
                {offering.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
